import styled from 'styled-components';

// Reusable Card Component — clean surface, hairline border, quiet shadow
export const Card = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.large};
  box-shadow: ${props => props.theme.shadows.card};
  border: 1px solid ${props => props.theme.colors.border.light};
  padding: ${props => props.padding || props.theme.spacing.lg};
  margin: ${props => props.margin || '0'};
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  ${props => props.hoverable && `
    cursor: pointer;

    &:hover {
      border-color: ${props.theme.colors.border.medium};
      box-shadow: ${props.theme.shadows.medium};
      transform: translateY(-1px);
    }
  `}

  ${props => props.active && `
    border-color: ${props.theme.colors.primary};
    box-shadow: ${props.theme.shadows.medium};
  `}
`;

// Button Component — solid brand fill, quiet hover, no shimmer / uppercase
export const ModernButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.sm};
  background: ${props => {
    if (props.variant === 'secondary') return props.theme.colors.secondary;
    if (props.variant === 'outline') return 'transparent';
    return props.theme.colors.primary;
  }};
  color: ${props => {
    if (props.variant === 'secondary') return props.theme.colors.text.primary;
    if (props.variant === 'outline') return props.theme.colors.primary;
    return props.theme.colors.text.inverse;
  }};
  border: 1px solid ${props => {
    if (props.variant === 'secondary') return props.theme.colors.border.medium;
    return props.theme.colors.primary;
  }};
  padding: ${props => {
    if (props.size === 'small') return '6px 12px';
    if (props.size === 'large') return '12px 24px';
    return '9px 16px';
  }};
  border-radius: ${props => props.theme.borderRadius.medium};
  font-size: ${props => {
    if (props.size === 'small') return '0.85rem';
    if (props.size === 'large') return '1rem';
    return '0.9rem';
  }};
  font-weight: 600;
  cursor: pointer;
  font-family: ${props => props.theme.fonts.main};
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${props => {
      if (props.variant === 'secondary') return '#e7ddc4';
      if (props.variant === 'outline') return props.theme.colors.primarySoft;
      return props.theme.colors.primaryHover;
    }};
    border-color: ${props => {
      if (props.variant === 'secondary') return props.theme.colors.border.dark;
      return props.theme.colors.primaryHover;
    }};
  }

  &:active {
    transform: translateY(1px);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primarySoft};
  }

  &:disabled {
    background: ${props => props.theme.colors.border.light};
    color: ${props => props.theme.colors.text.light};
    border-color: ${props => props.theme.colors.border.light};
    cursor: not-allowed;
  }
`;

// Page Header — clean title block, no gradient bar
export const PageHeader = styled.div`
  background: ${props => props.theme.colors.surface};
  padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing.xl};
  margin-bottom: ${props => props.theme.spacing.xl};
  border-radius: ${props => props.theme.borderRadius.large};
  box-shadow: ${props => props.theme.shadows.card};
  border: 1px solid ${props => props.theme.colors.border.light};

  h1, h2, h3 {
    margin: 0;
    color: ${props => props.theme.colors.text.primary};
    font-weight: 700;
    letter-spacing: -0.01em;
  }

  p {
    margin: ${props => props.theme.spacing.xs} 0 0 0;
    color: ${props => props.theme.colors.text.secondary};
    font-size: 0.95rem;
  }
`;

// Input Component
export const ModernInput = styled.input`
  padding: 10px 12px;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.borderRadius.medium};
  font-size: 0.95rem;
  font-family: ${props => props.theme.fonts.main};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text.primary};
  width: 100%;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primarySoft};
  }

  &::placeholder {
    color: ${props => props.theme.colors.text.light};
  }

  &.error {
    border-color: ${props => props.theme.colors.status.error};
    box-shadow: 0 0 0 3px rgba(197, 55, 44, 0.12);
  }
`;

// Select Component
export const ModernSelect = styled.select`
  padding: 10px 12px;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.borderRadius.medium};
  font-size: 0.95rem;
  font-family: ${props => props.theme.fonts.main};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text.primary};
  width: 100%;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primarySoft};
  }
`;

// Loading Spinner Component
export const LoadingSpinner = styled.div`
  width: ${props => props.size || '24px'};
  height: ${props => props.size || '24px'};
  border: 3px solid ${props => props.theme.colors.border.light};
  border-top: 3px solid ${props => props.theme.colors.primary};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: ${props => props.margin || '0 auto'};

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Badge Component — soft pill, sentence case
export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1.7;

  ${props => {
    switch (props.variant) {
      case 'success':
        return `
          background: ${props.theme.colors.status.success}18;
          color: ${props.theme.colors.status.success};
          border: 1px solid ${props.theme.colors.status.success}33;
        `;
      case 'warning':
        return `
          background: ${props.theme.colors.status.warning}18;
          color: ${props.theme.colors.status.warning};
          border: 1px solid ${props.theme.colors.status.warning}33;
        `;
      case 'error':
        return `
          background: ${props.theme.colors.status.error}18;
          color: ${props.theme.colors.status.error};
          border: 1px solid ${props.theme.colors.status.error}33;
        `;
      case 'info':
        return `
          background: ${props.theme.colors.status.info}18;
          color: ${props.theme.colors.status.info};
          border: 1px solid ${props.theme.colors.status.info}33;
        `;
      default:
        return `
          background: ${props.theme.colors.surfaceAlt};
          color: ${props.theme.colors.text.secondary};
          border: 1px solid ${props.theme.colors.border.light};
        `;
    }
  }}
`;
